import React, { useState, useEffect, FC } from 'react';
import s from './QuantityCards.module.scss';
import { usewpdataSelectors } from 'lib/wpdataState/selectors';
import { useCartActions } from 'lib/cartState/actions';
import { useCartSelectors } from 'lib/cartState/selectors';
import { ProductCartItem } from '@lib/cartState/slice';
import { BundleItem, FakeProduct } from '@lib/productsState/productsTypes';
import BestSellerBadge from '../../Misc/BestSellerBadge';
import b from '../../Misc/Badge.module.scss';
import Dot from '../Icons/Dot';
import { useIsDesktop } from 'utils/helpers';
import glideImage from '../../../utils/glideImage';

interface QuantityCardProps {
  id: string;
  amount: string | number;
  price: string | number;
  old_price: string | number;
  status: string;
  display_name: string;
  image: string;
  is_label_enabled?: boolean;
  description?: string;
  setSelectedProduct: React.Dispatch<React.SetStateAction<ProductCartItem | null>>;
  setActive: React.Dispatch<React.SetStateAction<string>>;
  active?: boolean;
  thank_you_displayed_name: string;
  bundle_products: BundleItem[];
  best_seller: boolean;
  best_value: boolean;
  warranty_price: number;
  shipping_price: number;
  generic_name: string;
  fake_products: FakeProduct[] | undefined;
}

const QuantityCardBundles: FC<QuantityCardProps> = ({
  id,
  amount,
  price,
  old_price,
  status,
  display_name,
  description,
  image,
  setSelectedProduct,
  active,
  setActive,
  thank_you_displayed_name,
  bundle_products,
  best_seller,
  best_value,
  generic_name,
  warranty_price,
  shipping_price,
  fake_products,
}) => {
  const { Settings, hero, Cart } = usewpdataSelectors();
  const { addMainProductAction } = useCartActions();
  const { cartProducts } = useCartSelectors();

  const isDesktop = useIsDesktop();
  const emptyCart = Settings?.emptyCart;

  const [formattedPrice, setFormattedPrice] = useState<string | null>(null);
  const [formattedOldPrice, setFormattedOldPrice] = useState<string | null>(null);

  useEffect(() => {
    setFormattedPrice(Settings.formattedString.replace(/(0\.00|0,00|0)/, price.toString()));
    setFormattedOldPrice(Settings.formattedString.replace(/(0\.00|0,00|0)/, old_price.toString()));
  }, [price, old_price]);

  return (
    <div
      id={id}
      onClick={() => {
        setActive(id);
        const product = {
          id,
          amount: Number(amount),
          price: Number(price),
          old_price: Number(old_price),
          status,
          display_name,
          image,
          generic_name: generic_name ?? display_name,
          shipping_price: Number(shipping_price),
          description: description ?? '',
          orderSummaryName: thank_you_displayed_name,
          bundle_products: bundle_products,
          warranty_price: warranty_price,
          thank_you_displayed_name,
          fake_products: fake_products,
          type: 'offer',
        };

        if (emptyCart) {
          setSelectedProduct(product);
        } else {
          if (cartProducts.find((item: ProductCartItem) => item.id === id)) return;
          addMainProductAction(product, emptyCart);
        }
      }}
      style={
        {
          '--badge-best-seller': hero.styles.bestSellerColor,
          '--badge-best-value': hero.styles.bestValueColor,
          '--active-border-color': hero.styles.activeColor,
        } as React.CSSProperties & { [key: string]: string | number }
      }
      className={`${s.card} ${active ? s['card--active'] : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
        }
      }}
      tabIndex={0}
      role="button"
    >
      {best_seller && (
        <div className={`${b['badge--mobile']} ${b['badge--mobile--seller']}`.trim()}>{Cart.bestSellerLabel}</div>
      )}{' '}
      {best_value && (
        <div className={`${b['badge--mobile']} ${b['badge--mobile--value']}`.trim()}>{Cart.bestValueLabel}</div>
      )}
      <label htmlFor={String(amount)} className={`${s.card__content} ${active ? s['card--selected'] : ''}`}>
        <div className={s.card__leftSide}>
          <input
            className={s.card__input}
            type="radio"
            name={String(id)}
            style={{ background: hero.styles.radioColor }}
            checked={active || false}
            id={id}
          />
          <div className={s.card__customRadioBtn} style={{ borderColor: hero.styles.radioColor }}>
            <div className={s.card__customInnerRabioBtn} style={{ background: hero.styles.radioColor }} />
          </div>
          <img src={glideImage(image, { w: 48, h: 48 })} className={s.card__img} alt={Settings.brandName} />
          <div className={s.card__titleBox}>
            <div className={s.title} style={{ color: hero.styles.textColorPrimary }}>
              {display_name}
            </div>
            {description && (
              <div className={s.description} style={{ color: hero.styles.textColorItemsLeft }}>
                <Dot className={s.itemsLeftCircle} fill={hero.styles.textColorItemsLeft} />
                {description}
              </div>
            )}
          </div>
          {best_seller && isDesktop && (
            <BestSellerBadge text={Cart.bestSellerLabel} isBestSeller={true} modifiers={{ marginRight: '0px' }} />
          )}
          {best_value && isDesktop && (
            <BestSellerBadge text={Cart.bestValueLabel} isBestSeller={true} modifiers={{ marginRight: '0px' }} />
          )}
        </div>

        <div className={s.card__rightSide}>
          <div className={s.card__price}>
            <span style={{ color: hero.styles.textColorPrimary, whiteSpace: 'nowrap' }}>{formattedPrice}</span>
            {!Settings.showEachLabel && (
              <p className={s.card__eachText} style={{ color: hero.styles.priceColor }}>
                /{hero.each_label}
              </p>
            )}
          </div>
          <div className={s.discount} style={{ color: hero.styles.oldPriceColor }}>
            {formattedOldPrice}
          </div>
        </div>
      </label>
    </div>
  );
};

export default QuantityCardBundles;
